/**
 * API client for the do-not-call list.
 *
 * Its own file rather than more lines in `lib/calls.ts`, which was split for
 * size in the same week: this is a small, self-contained surface that a call
 * happens to consult, not part of what a call is.
 */
import { api } from "./api";

/** Why a number is on the list. The first two cannot be taken off again. */
export type PhoneSuppressionSource = "unsubscribe" | "complaint" | "manual" | "import";

/**
 * Who the number turned out to be, read off the calls we made to it.
 *
 * Every field can be null on its own: a number added by hand and never called
 * has no party at all, and a number called only from the dial pad has calls but
 * no name. Both are honest answers, not missing data.
 */
export interface PhoneSuppressionParty {
  companyName: string | null;
  clientId: string | null;
  leadId: string | null;
  lastCallId: string | null;
  lastCallAt: string | null;
  calls: number;
}

export interface PhoneSuppression {
  id: string;
  /** E.164, the same shape the dial guard compares against. */
  value: string;
  source: PhoneSuppressionSource | string;
  reason: string | null;
  note: string | null;
  createdBy: string | null;
  /** The operator who wrote it down, resolved by the server. */
  createdByName: string | null;
  createdAt: string;
  /** The person asked for it, so nobody here may remove it. */
  permanent: boolean;
  /** Only filled in for a caller that asked for it - see `listPhoneSuppression`. */
  party: PhoneSuppressionParty | null;
}

/**
 * The whole list.
 *
 * `context` also asks the server who each number is, which costs two more
 * indexed reads. The shell's strike-through fetch leaves it off: it needs the
 * values and nothing else, and it runs for every operator at sign-in.
 */
export function listPhoneSuppression(opts?: { context?: boolean }): Promise<{ rows: PhoneSuppression[] }> {
  return api.get(`/admin/crm/calls/suppression${opts?.context ? "?context=1" : ""}`);
}

export function addPhoneSuppression(input: {
  phone: string;
  source?: "unsubscribe" | "complaint" | "manual";
  reason?: string;
  note?: string;
}): Promise<{ suppression: PhoneSuppression }> {
  return api.post("/admin/crm/calls/suppression", input);
}

export function removePhoneSuppression(id: string): Promise<{ ok: boolean }> {
  return api.del(`/admin/crm/calls/suppression/${encodeURIComponent(id)}`);
}
